/// <amd-module name="Wit/Search/FieldSuggestionStore" />
import Resources = require("Wit/Search/Resources");
import { ISearchFilter, ISearchFilterGroup } from "VSS/Features/Search/SearchHelp";
import { FieldType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingSuggestionField, ShortcutTexts, ShortcutFieldNames, ValueSuggestions } from "Wit/Search/SearchFields";
/**
* Set of characters which don't form a valid WIT Field name.
*/
const WitRestrictedCharacters = "\\.,;'`:~\\\\\/\\*\\|\\?\"&%$!\\+=\\(\\)\\[\\]{}<>-";
const ValueRestrictedCharacters = ":|<|>|<=|>=|!=";
const FullFormedRegex: RegExp = new RegExp("([^{0}\\s]+)\\s*(:|<=|>=|<|>|=|!=)\\s*([^{1}\\s]*)$"
    .replace("{0}", WitRestrictedCharacters)
    .replace("{1}", ValueRestrictedCharacters)); // e.g. ChangedDate: 2012-12-09  or AssignedTo: etc.
/**
 * Regular expression to capture field name inputs of type "Assigned etc.
 */
const OnlyFieldNameRegex: RegExp = new RegExp("([^{0}\\s]+)(\\s*)$".replace("{0}", WitRestrictedCharacters)); // e.g. AssignedTo etc.
const FullFormedRegexWithQuotes: RegExp = new RegExp("\"([^{0}]+)\"\\s*(:|<=|>=|<|>|=|!=)\\s*([^{1}\"]*)$"
    .replace("{0}", WitRestrictedCharacters)
    .replace("{1}", ValueRestrictedCharacters)); //e.g. "ChangedDate" : 2012-12-09 or "AssignedTo": etc.
const FieldWithinQuotesRegex: RegExp = new RegExp("\"([^{0}]+)\"\\s*$".replace("{0}", WitRestrictedCharacters)); // e.g "Assigned To"
const FullFormedRegexForUnpairedQuote: RegExp = /(.*)\s*(:|<=|>=|<|>|=|!=)\s*\"([^\"]*)$/; //e.g  AssignedTo:" , :" , >", ::" etc.
/**
 * Regular expression to capture field name inputs of type Assigned etc - Not enclosed within quotes(").
 */
const FieldNameRegexForUnpairedQuote: RegExp = new RegExp("\"[^{0}]*$".replace("{0}", WitRestrictedCharacters)); //e.g "Assigned etc.;
const OperatorsRegex: RegExp = /(:|<=|>=|<|>|=|!=)/;
const DelimiterCharacters: string[] = [' ', '<', '>', ':', '=', '"', "!"];

/**
* Type of suggestion the help supports.
*/
export enum DropdownType {
    None,

    Static,

    Fields,

    Operators,

    Values,

    Identity
}

/**
 * Filters to list down in the default help dropdown.
 */
export const defaultFieldsFilterGroup: ISearchFilterGroup = {
    caption: Resources.FilterByField,
    example: Resources.FilterByFieldExample,
    footer: Resources.FilterByFieldFooter,
    filters: [
        {
            text: `${ShortcutTexts[ShortcutFieldNames.AssignedTo]}:`,
            hint: Resources.AssignedToHint,
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: `${ShortcutTexts[ShortcutFieldNames.CreatedBy]}:`,
            hint: Resources.CreatedByHint,
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: `${ShortcutTexts[ShortcutFieldNames.State]}:`,
            hint: Resources.StateHint,
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: `${ShortcutTexts[ShortcutFieldNames.WorkItemType]}:`,
            hint: Resources.WITHint,
            getSuggestedText: appendSpaceToFilterText
        }
    ]
}

/**
 * Operators list to be shown in default and dynamic help dropdowns.
 */
export const defaultOperatorsFilterGroup: ISearchFilterGroup = {
    caption: Resources.Operators,
    example: Resources.OperatorsExample,
    singleLine: true,
    filters: [
        {
            text: "AND",
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: "NOT",
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: "OR",
            getSuggestedText: appendSpaceToFilterText
        }
    ]
}

const numericOperatorsFilterGroup: ISearchFilterGroup = {
    caption: "",
    filters: [
        {
            text: "=",
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: "!=",
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: ">",
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: "<",
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: ">=",
            getSuggestedText: appendSpaceToFilterText
        },
        {
            text: "<=",
            getSuggestedText: appendSpaceToFilterText
        }
    ]
}

interface IToken {
    content: string;

    startIndex: number;

    endIndex: number;
}

/**
 * Store to give relevant help suggestions based on the input text.
 */
export class FieldSuggestionStore {
    public dropdownType: DropdownType = DropdownType.Static;
    public textToFilter: string;

    public getFilterGroups = (inputText: string, fields: IWorkItemTrackingSuggestionField[]): ISearchFilterGroup[] => {
        this.textToFilter = this.getTextToFilter(inputText, fields);;
        if (this.dropdownType === DropdownType.Static) {
            return [
                defaultFieldsFilterGroup,
                defaultOperatorsFilterGroup
            ];
        } else if (this.dropdownType === DropdownType.Fields) {
            const filteredFields = fields.filter(f => normalize(f.field.name).indexOf(normalize(this.textToFilter)) === 0);
            return [
                {
                    caption: Resources.FilterByField,
                    example: Resources.FilterByFieldExample,
                    filters: filteredFields.map<ISearchFilter>(f => {
                        return {
                            text: f.field.name,
                            getSuggestedText: (currentString: string, filterText: string) =>
                                getSuggestedText(currentString, filterText, isStringType(f.field.type)),
                            shortcutText: f.shortcut,
                            replaceTextOnActivation: true
                        };
                    })
                },
                defaultOperatorsFilterGroup
            ];
        } else if (this.dropdownType === DropdownType.Operators) {
            return [numericOperatorsFilterGroup]
        } else if (this.dropdownType === DropdownType.Values) {
            return [{
                caption: "",
                filters: [
                    {
                        text: ValueSuggestions.Today,
                        getSuggestedText,
                        replaceTextOnActivation: true
                    },
                    {
                        text: ValueSuggestions.Today1,
                        getSuggestedText,
                        replaceTextOnActivation: true
                    },
                    {
                        text: ValueSuggestions.Today7,
                        getSuggestedText,
                        replaceTextOnActivation: true
                    },
                    {
                        text: ValueSuggestions.Today30,
                        getSuggestedText,
                        replaceTextOnActivation: true
                    }
                ].filter(f => normalize(f.text).indexOf(normalize(this.textToFilter)) === 0)
            }]
        }

        return [];
    }

    /**
    * Return the text on which the suggestions need filtering.
    */
    private getTextToFilter = (text: string, fields: IWorkItemTrackingSuggestionField[]): string => {
        // Default dropdown type is always a static one.
        this.dropdownType = DropdownType.Static;

        // To show the initial drop down when there is no text in search box or only a single quote
        if (!text || (text.length <= 1 && isQuoteUnpaired(text))) {
            return "";
        }

        if (isQuoteUnpaired(text)) {
            return this.handleForUnPairedQuote(text, fields);
        } else {
            return this.handleForPairedQuote(text, fields);
        }
    }

    private handleForPairedQuote = (text: string, fields: IWorkItemTrackingSuggestionField[]): string => {
        const lastIndexOfQuote = text.lastIndexOf("\"");
        const fullFormRegexDelegate = (regexMatch: RegExpMatchArray) => {
            const probableFieldName = regexMatch[1], value = regexMatch[3];
            return this.setSuggestionModeForOperators(probableFieldName, value, fields);
        };
        const fieldNameRegexDelegate = (regexMatch: RegExpMatchArray) => {
            // Checking if more than one space is given or not
            const hasSpace = regexMatch[2] && regexMatch[2].replace(/\s+/, " ") === " ",
                fieldName = regexMatch[1];
            if (hasSpace) {
                return this.setSuggestionModeForFieldName(fieldName, fields);
            } else {
                this.dropdownType = DropdownType.Fields;
                return fieldName;
            }
        };

        // If there are no Quotes in the text then we need to take care of that particular cases
        if (lastIndexOfQuote < 0) {
            const fullFormRegExMatch = text.match(FullFormedRegex);
            if (fullFormRegExMatch) {
                return fullFormRegexDelegate(fullFormRegExMatch);
            }

            const fieldNameMatch = text.match(OnlyFieldNameRegex);
            if (fieldNameMatch) {
                return fieldNameRegexDelegate(fieldNameMatch);
            }

            this.dropdownType = DropdownType.Fields;
            return text;
        } else {
            // Getting text after Quote
            const textAfterQuote = text.substr(lastIndexOfQuote + 1),
                fullFormRegExMatch = textAfterQuote.match(FullFormedRegex);
            // e.g   "Accepted" DueDate : 
            if (fullFormRegExMatch) {
                return fullFormRegexDelegate(fullFormRegExMatch);
            }

            const fullFormedRegexWithQuotesMatch = text.match(FullFormedRegexWithQuotes);
            //e.g. "ChangedDate" : 2012-12-09 or "AssignedTo": etc.
            if (fullFormedRegexWithQuotesMatch) {
                // To give suggestions for values if field in quotes is date time
                const probableFieldName = fullFormedRegexWithQuotesMatch[1],
                    value = fullFormedRegexWithQuotesMatch[3];
                return this.setSuggestionModeForOperators(probableFieldName, value, fields);
            }

            // To give suggestions to normal fields after quotes e.g "assigned to" priority
            const fieldNameMatch = textAfterQuote.match(OnlyFieldNameRegex);
            if (fieldNameMatch) {
                return fieldNameRegexDelegate(fieldNameMatch);
            }

            // Operator suggestions for field names in quotes e.g "Priority" 
            const fieldNameWithinQuotesMatch = text.match(FieldWithinQuotesRegex);
            if (fieldNameWithinQuotesMatch) {
                const probableFieldName = fieldNameWithinQuotesMatch[1].replace(/\s+/g, "");
                return this.setSuggestionModeForFieldName(probableFieldName, fields);
            }

            this.dropdownType = DropdownType.Static;

            return "";
        }
    }

    private handleForUnPairedQuote = (text: string, fields: IWorkItemTrackingSuggestionField[]): string => {
        const fullFormRegExMatch = text.match(FullFormedRegexForUnpairedQuote);
        const fieldnameRegexMatch = text.match(FieldNameRegexForUnpairedQuote);

        // If Quotes are preceded by an operator then no suggestions need to be given except if the field value is identity picker before Operator
        if (fullFormRegExMatch) {
            //Checking if Operator is Colon or not as Identity Picker Field Value is followed by colon only
            if (fullFormRegExMatch[2] === ":") {  //e.g : "Assigned to" : "Alias" , "Due Date" : 20-10-2016 etc.
                //this to to trim spaces in the match there are cases where spaces are not getting selected seperately
                const fieldValueBeforeColon = fullFormRegExMatch[1].trim();
                const stringLength: number = fieldValueBeforeColon.length;
                let probableFieldName, currentPosition: number;
                //Checking if the field value is given in quotes or not
                if (fieldValueBeforeColon[stringLength - 1] === "\"") {
                    currentPosition = stringLength - 2;
                    //Getting the position of the other Quote that is at the beginning of the field value
                    while (fieldValueBeforeColon.charAt(currentPosition) !== "\"" && currentPosition > 0) {
                        currentPosition--;
                    }

                    //Getting field value before colon to check if it is a identity picker
                    probableFieldName = fieldValueBeforeColon.substring(currentPosition + 1, stringLength - 1);
                } else {
                    // Checking if field value is given in a normal way
                    currentPosition = stringLength - 1;
                    //Getting the position of the beginning of the field value
                    //e.g "assignedto":"User, assignedto : "user , "Sometext"Assignedto : "user, some text Assignedto:"User
                    while (fieldValueBeforeColon.charAt(currentPosition) !== " " && currentPosition > 0) {
                        currentPosition--;
                    }

                    //Condition if the value is given at beginning with out space
                    if (fieldValueBeforeColon.charAt(currentPosition) === " ") {
                        currentPosition++;
                    }

                    probableFieldName = fieldValueBeforeColon.substr(currentPosition);
                }

                return this.setSuggestionModeForOperators(probableFieldName, fullFormRegExMatch[3], fields);
            } else {
                this.dropdownType = DropdownType.None;

                return "";
            }
        }

        // If a valid fieldName is preceded by a single quote then we need to remove spaces in filedName and send to filter for suggestions
        if (fieldnameRegexMatch) {
            const lastIndexOfQuote = text.lastIndexOf("\""),
                textAfterQuote = text.substr(lastIndexOfQuote + 1);
            // e.g "Assigned to : , "Tags = , ": etc.
            if (!textAfterQuote) {
                this.dropdownType = DropdownType.Static;
            } else if (textAfterQuote.match(OperatorsRegex)) {
                this.dropdownType = DropdownType.None;
            } else {
                this.dropdownType = DropdownType.Fields;
            }

            return textAfterQuote;
        }

        this.dropdownType = DropdownType.None;

        return "";
    }

    private setSuggestionModeForOperators = (probableFieldName: string, fieldValue: string, fields: IWorkItemTrackingSuggestionField[]): string => {
        const fieldType = getFieldType(fields, probableFieldName);
        if (typeof fieldType === "undefined") {
            this.dropdownType = DropdownType.None;
            return "";
        } else if (fieldType === FieldType.DateTime) {
            this.dropdownType = DropdownType.Values;
        }
        else if (fieldType === FieldType.Identity || isIdentityField(fields, probableFieldName)) {
            // Corresponds to fieldType.Identity - Not downloading contracts as it bloats up our bundle size.
            this.dropdownType = DropdownType.Identity;
        } else {
            this.dropdownType = DropdownType.None;
        }

        return fieldValue;
    }

    private setSuggestionModeForFieldName = (fieldName: string, fields: IWorkItemTrackingSuggestionField[]): string => {
        // If field Type is numeric or date time then mode is operatorSuggesion else mode is none
        const fieldType = getFieldType(fields, fieldName);
        if (!fieldType) {
            this.dropdownType = DropdownType.Static;
        } else if (fieldType === FieldType.DateTime || fieldType === FieldType.Double || fieldType === FieldType.Integer) {
            this.dropdownType = DropdownType.Operators;
        }

        return "";
    }
}

function normalize(text: string): string {
    return text && text.replace(/\s+/g, "").toLowerCase();
}

function getFieldType(fields: IWorkItemTrackingSuggestionField[], fieldName: string): FieldType | undefined {
    const filteredFields = getField(fields, fieldName);
    return filteredFields[0] && filteredFields[0].field ? filteredFields[0].field.type : undefined;
}

/**
 * Given the string returns the "start" and "end index" of the last token(content) to be replaced
 * @param str
 * @param delimiters
 */
function getToken(str: string, delimiters: Array<string>): IToken {
    let tokenStart = str.length - 1;
    let tokenEnd = str.length - 1;
    // get the start Index
    while (tokenStart > 0 && delimiters.indexOf(str.charAt(tokenStart)) < 0) {
        tokenStart--;
    }

    // get the end Index
    while (tokenEnd < str.length - 1 && delimiters.indexOf(str.charAt(tokenEnd)) === -1) {
        tokenEnd++;
    }

    const startIndex = tokenStart > 0 ? tokenStart + 1 : 0;
    const endIndex = tokenEnd < str.length - 1 ? tokenEnd - 1 : tokenEnd;

    return {
        startIndex: startIndex,
        endIndex: endIndex,
        content: str.substr(startIndex, endIndex - startIndex + 1)
    };
}

function isQuoteUnpaired(text: string): boolean {
    const quotesMatch = text.match(/\"/g);
    return !!((quotesMatch ? quotesMatch.length : 0) % 2);
}

function isStringType(fieldType: FieldType): boolean {
    return fieldType === FieldType.Guid ||
        fieldType === FieldType.History ||
        fieldType === FieldType.Html ||
        fieldType === FieldType.PlainText ||
        fieldType === FieldType.String ||
        fieldType === FieldType.TreePath ||
        fieldType === FieldType.Identity ||
        fieldType === FieldType.PicklistString;
}

/**
 * Given current input text, text of the filter which is activated returns a final string with new suggested text in place.
 * @param currentString
 * @param filterText
 * @param removeWhiteSpaces
 */
export function getSuggestedText(currentString: string, filterText: string, appendColon: boolean = false, removeWhiteSpaces: boolean = true): string {
    // Remove all white spaces.
    filterText = removeWhiteSpaces ? filterText.replace(/[\s]+/g, "") : filterText;

    // If there is an unpaired quote in the string. Use " as the delimiter.
    const delimiters = isQuoteUnpaired(currentString) ? ['"'] : DelimiterCharacters;

    let { endIndex, startIndex } = getToken(currentString, delimiters);
    // Removing Single Quote from the string if it contains odd number of quotes.
    // As suggested text contains quotes surrounded which will give extra quote in resulted string.
    if (startIndex > 0 &&
        currentString.charAt(startIndex - 1) === "\"" &&
        isQuoteUnpaired(currentString)) {
        currentString = currentString.slice(0, startIndex - 1) + currentString.slice(startIndex);
        startIndex--;
        endIndex--;
    }

    if (appendColon) {
        // Append ":" at the end of suggested text.
        filterText = `${filterText}:`;
    }
    filterText = `${filterText} `;

    const prevSubString = currentString.substr(0, startIndex).trim();
    const nextSubString = currentString.substr(endIndex + 1).trim();
    const stringUptoSuggestion = `${prevSubString}${prevSubString !== "" ? " " : ""}${filterText}`;
    const stringWithSuggestion = `${stringUptoSuggestion}${nextSubString}`;

    return stringWithSuggestion;
}

function appendSpaceToFilterText(currentString: string, filterText: string): string {
    return `${filterText} `;
}

function isIdentityField(fields: IWorkItemTrackingSuggestionField[], fieldName: string): boolean {
    const filteredFields = getField(fields, fieldName);
    return filteredFields[0].field && filteredFields[0].field.isIdentity;
}

function getField(fields: IWorkItemTrackingSuggestionField[], fieldName: string): IWorkItemTrackingSuggestionField[] {
    return fields.filter(f =>
        normalize(f.field.name) === normalize(fieldName) || (f.shortcut && normalize(f.shortcut) === normalize(fieldName)));
}